// ============================================
// LightCullPass — Forward+ tile-based light culling (compute)
// ============================================
//
// Divides the viewport into 16×16 pixel tiles and runs a compute shader
// to determine which lights affect each tile. The resulting per-tile light
// index buffer is then read by the `fs_forward_plus` fragment shader.
//
// Usage:
// ```ts
// const cullPass = new LightCullPass(ctx);
// // Per frame, after LightManager.end():
// cullPass.cull(lightManager, viewportWidth, viewportHeight);
// // Bind tile data for forward+ rendering:
// ctx.setForwardPlusData!(lightManager.uniformBuffer, lightManager.lightBuffer, cullPass.tileLightDataBuffer);
// ```

import type { IRenderContext } from '../core/IRenderContext';
import type { LightManager } from './LightManager';
import type { Camera2D } from '../objects/Camera2D';
import type { BufferHandle, ComputePipelineHandle, ShaderHandle } from '../types';
import { NULL_HANDLE } from '../types';
import { defineStruct, GPUType } from '../core/GPUTypes';
import { graphicsConfig } from '../GraphicsConfig';
import { getCompiledShader } from '../pipeline/ShaderCache';

// ============================================================================
// Constants — derived from GraphicsConfig
// ============================================================================

/** Tile size in pixels (each tile is TILE_SIZE × TILE_SIZE). */
export const TILE_SIZE = graphicsConfig.tileSize;

/** Maximum number of light indices stored per tile. */
export const MAX_LIGHTS_PER_TILE = graphicsConfig.maxLightsPerTile;

/** Storage stride per tile: 1 (count) + MAX_LIGHTS_PER_TILE (indices). */
export const TILE_STRIDE = 1 + graphicsConfig.maxLightsPerTile;

// ============================================================================
// CullUniforms struct (matches light-cull.wgsl)
// ============================================================================

const CULL_UNIFORMS = defineStruct('CullUniforms', {
  tileCountX:        GPUType.u32().build(),
  tileCountY:        GPUType.u32().build(),
  totalLights:       GPUType.u32().build(),
  _pad0:             GPUType.u32().build(),
  screenSize:        GPUType.f32().vec2(),
  invScreenSize:     GPUType.f32().vec2(),
  invViewProjection: GPUType.f32().mat4x4(),
  // Appended config (was pipeline overrides; now a uniform read by light-cull.slang — see sprite-lit.slang).
  tileSize:          GPUType.u32().build(),
  maxLightsPerTile:  GPUType.u32().build(),
});

// ============================================================================
// LightCullPass
// ============================================================================

export class LightCullPass {
  private ctx: IRenderContext;

  // ---- GPU resources ----
  private computeShader: ShaderHandle = NULL_HANDLE;
  private computePipeline: ComputePipelineHandle = NULL_HANDLE;
  private _cullUniformBuffer: BufferHandle = NULL_HANDLE;
  private _tileLightBuffer: BufferHandle = NULL_HANDLE;

  // ---- CPU staging ----
  private readonly uniformWriter = CULL_UNIFORMS.createWriter();
  private readonly _invVP = new Float32Array(16);
  private readonly _uniformF32 = new Float32Array(CULL_UNIFORMS.size / 4);

  // ---- Current dimensions ----
  private _tileCountX = 0;
  private _tileCountY = 0;
  private _totalTiles = 0;
  // Dimensions the tile buffer was last ALLOCATED for. Reallocate when either changes — the buffer size is
  // totalTiles × (1 + maxLightsPerTile), so a runtime bump of maxLightsPerTile OR tileSize (→ totalTiles)
  // must trigger a resize, not just a viewport change.
  private _allocTotalTiles = -1;
  private _allocMaxLightsPerTile = -1;

  constructor(ctx: IRenderContext) {
    this.ctx = ctx;

    if (!ctx.supportsCompute) {
      console.warn('[LightCullPass] Compute shaders not supported — Forward+ unavailable');
      return;
    }

    // Create compute shader (precompiled Slang → WGSL from the startup cache).
    this.computeShader = ctx.createComputeShader!(getCompiledShader('lighting/light-cull'));

    // Create compute pipeline
    // Bindings: uniform(0), storage(1)=lights, storage(2)=tileLightData
    this.computePipeline = ctx.createComputePipeline!({
      shader: this.computeShader,
      entryPoint: 'main',
      workgroupSize: [64, 1, 1],
      storageBufferCount: 2,
      label: 'light-cull-pipeline',
    });

    // Create cull uniform buffer
    this._cullUniformBuffer = ctx.createBuffer({
      size: CULL_UNIFORMS.size,
      usage: ['uniform', 'copy-dst'],
      label: 'light-cull-uniforms',
    });
  }

  // -----------------------------------------------------------------------
  // Accessors
  // -----------------------------------------------------------------------

  /** Per-tile light index storage buffer for forward+ rendering. */
  get tileLightDataBuffer(): BufferHandle { return this._tileLightBuffer; }

  /** Number of tiles in X direction. */
  get tileCountX(): number { return this._tileCountX; }

  /** Number of tiles in Y direction. */
  get tileCountY(): number { return this._tileCountY; }

  /** Whether the pass is ready to cull. */
  get isReady(): boolean { return this.computePipeline !== NULL_HANDLE; }

  // -----------------------------------------------------------------------
  // Per-Frame Culling
  // -----------------------------------------------------------------------

  /**
   * Run tile-based light culling.
   *
   * Must be called after `lightManager.end()` and before forward+ rendering.
   * Issues a compute dispatch and flushes so results are available to fragment shaders.
   *
   * @param camera - Camera for inverse view-projection matrix
   * @param lightManager - LightManager with finalized per-frame light data
   * @param viewportWidth - Viewport width in pixels
   * @param viewportHeight - Viewport height in pixels
   */
  cull(camera: Camera2D, lightManager: LightManager, viewportWidth: number, viewportHeight: number): void {
    if (!this.isReady) return;
    // NOTE: Do NOT early-return when visibleLightCount === 0.
    // The compute dispatch must run to clear stale tile data (write count=0 per tile).

    const ctx = this.ctx;

    // Recalculate tile grid
    const tileCountX = Math.ceil(viewportWidth / graphicsConfig.tileSize);
    const tileCountY = Math.ceil(viewportHeight / graphicsConfig.tileSize);
    const totalTiles = tileCountX * tileCountY;
    const maxLightsPerTile = graphicsConfig.maxLightsPerTile;

    this._tileCountX = tileCountX;
    this._tileCountY = tileCountY;
    this._totalTiles = totalTiles;

    // (Re)allocate the tile buffer when the tile count (viewport / tileSize) OR the per-tile light capacity
    // changed. The buffer is totalTiles × (1 + maxLightsPerTile) u32s; the cull + accumulation shaders index it
    // with the LIVE maxLightsPerTile stride. Bumping maxLightsPerTile (or tileSize) at runtime without resizing
    // left the buffer undersized, so high tile indices overflowed it → the lower part of the viewport read
    // out-of-bounds (→ 0 lights) and rendered fully unlit. That's the "half the screen isn't lit" / viewport-
    // locked-blocks bug.
    if (
      this._tileLightBuffer === NULL_HANDLE ||
      totalTiles !== this._allocTotalTiles ||
      maxLightsPerTile !== this._allocMaxLightsPerTile
    ) {
      this.resizeTileBuffer(totalTiles);
      this._allocTotalTiles = totalTiles;
      this._allocMaxLightsPerTile = maxLightsPerTile;
    }

    // Get inverse view-projection from camera (reuse pre-allocated array)
    const invVP = this._invVP;
    camera.getInverseViewProjection(invVP);

    // Update uniforms
    this.uniformWriter.setAll({
      tileCountX,
      tileCountY,
      totalLights: lightManager.visibleLightCount,
      _pad0: 0,
      screenSize: [viewportWidth, viewportHeight],
      invScreenSize: [1 / viewportWidth, 1 / viewportHeight],
      invViewProjection: invVP,
      tileSize: graphicsConfig.tileSize,
      maxLightsPerTile: graphicsConfig.maxLightsPerTile,
    });

    // Copy to pre-allocated F32 and upload (no per-frame allocation)
    const src = new Uint8Array(this.uniformWriter.buffer);
    const dst = new Uint8Array(this._uniformF32.buffer);
    dst.set(src);
    ctx.updateBuffer(this._cullUniformBuffer, this._uniformF32);

    // Dispatch compute
    ctx.setComputePipeline!(this.computePipeline);
    ctx.bindComputeResources!({
      uniforms: [this._cullUniformBuffer],
      storageBuffers: [
        { buffer: lightManager.lightBuffer },
        { buffer: this._tileLightBuffer },
      ],
    });

    const workgroups = Math.ceil(totalTiles / 64);
    ctx.dispatchCompute!({ x: workgroups });

    // Flush compute so tile data is available for fragment reads.
    ctx.flushCompute!();
  }

  // -----------------------------------------------------------------------
  // Internal
  // -----------------------------------------------------------------------

  /**
   * (Re-)create the tile light data storage buffer.
   */
  private resizeTileBuffer(totalTiles: number): void {
    const ctx = this.ctx;

    // Destroy old buffer
    if (this._tileLightBuffer !== NULL_HANDLE) {
      ctx.destroyBuffer(this._tileLightBuffer);
    }

    // Allocate: totalTiles * TILE_STRIDE * 4 bytes (u32 per entry)
    const tileStride = 1 + graphicsConfig.maxLightsPerTile;
    const sizeBytes = totalTiles * tileStride * 4;
    this._tileLightBuffer = ctx.createBuffer({
      size: sizeBytes,
      usage: ['storage', 'copy-dst'],
      label: 'tile-light-data',
    });
  }

  // -----------------------------------------------------------------------
  // Destroy
  // -----------------------------------------------------------------------

  /** Release all GPU resources. */
  destroy(): void {
    const ctx = this.ctx;
    if (this._tileLightBuffer !== NULL_HANDLE) {
      ctx.destroyBuffer(this._tileLightBuffer);
      this._tileLightBuffer = NULL_HANDLE;
    }
    if (this._cullUniformBuffer !== NULL_HANDLE) {
      ctx.destroyBuffer(this._cullUniformBuffer);
      this._cullUniformBuffer = NULL_HANDLE;
    }
    if (this.computePipeline !== NULL_HANDLE) {
      ctx.destroyComputePipeline!(this.computePipeline);
      this.computePipeline = NULL_HANDLE;
    }
    if (this.computeShader !== NULL_HANDLE) {
      ctx.destroyShader(this.computeShader);
      this.computeShader = NULL_HANDLE;
    }
  }
}
